import * as React from 'react'
import { unwindEdges } from '@good-idea/unwind-edges'

import {
  Product,
  ShopifyStorefrontImage,
  ShopifyStorefrontProductVariant,
} from '@/types'
import {
  ProductImagesCurrentImage,
  ProductImagesThumbnails,
  ProductImagesWrapper,
} from './styles'
import { ShopifyImage } from '@/components/ShopifyImage'
import { ProductImageZoom } from '../ProductImageZoom'

const { useEffect, useState } = React

interface ProductImagesProps {
  product: Product
  selectedVariant: ShopifyStorefrontProductVariant
}

const getSelectedVariantImage = (
  images: ShopifyStorefrontImage[],
  imageId?: string | null,
): ShopifyStorefrontImage | undefined => {
  if (!imageId) return undefined
  return images.find((i) => i.id === imageId)
}

export const ProductImages: React.FC<ProductImagesProps> = ({
  product,
  selectedVariant,
}) => {
  const [productImages] = unwindEdges(product.shopify.images)
  const [selectedZoomImageId, setSelectedZoomImageId] = useState<string | null>(
    null,
  )

  const closeZoom = () => setSelectedZoomImageId(null)

  const [selectedImage, setSelectedImage] = useState(
    /* Prefer the image of the selected variant, otherwise
     * fall back to the first one */
    getSelectedVariantImage(productImages, selectedVariant?.image?.id) ||
      productImages[0],
  )

  useEffect(() => {
    /* Change to the selected variant's image on change */
    const newImage = getSelectedVariantImage(
      productImages,
      selectedVariant?.image?.id,
    )
    if (newImage) {
      setSelectedImage(newImage)
    }
  }, [selectedVariant?.image?.id])

  const handleSelectImage = (newImage: ShopifyStorefrontImage) => () =>
    setSelectedImage(newImage)

  const handleSelectZoomImage = (image: ShopifyStorefrontImage) => () =>
    setSelectedZoomImageId(image.id || null)

  return (
    <>
      {selectedZoomImageId ? (
        <ProductImageZoom
          close={closeZoom}
          images={productImages}
          selectedImageId={selectedZoomImageId}
        />
      ) : null}
      <ProductImagesWrapper>
        <ProductImagesThumbnails>
          {
            /* Do not render thumbnails if there is only one image.
             * However, we still want to render the container so it takes up
             * the first column in the grid. */
            productImages.length > 1
              ? productImages.map((image) => (
                  <button key={image.id} onClick={handleSelectImage(image)}>
                    <ShopifyImage image={image} />
                  </button>
                ))
              : null
          }
        </ProductImagesThumbnails>
        <ProductImagesCurrentImage>
          <button onClick={handleSelectZoomImage(selectedImage)}>
            <ShopifyImage image={selectedImage} />
          </button>
        </ProductImagesCurrentImage>
      </ProductImagesWrapper>
    </>
  )
}
